import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import api from "../lib/api";
import { useT, useUI, useLang } from "../lib/i18n";
import SEO from "../components/site/SEO";

// Configure DOMPurify to allow safe blog HTML produced by Tiptap editor
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "h1", "h2", "h3", "h4", "strong", "em", "u", "s", "ul", "ol", "li", "blockquote", "a", "img", "br", "hr", "code", "pre", "span"],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel"],
  ALLOW_DATA_ATTR: false,
};

export default function BlogPost() {
  const { slug } = useParams();
  const t = useT();
  const ui = useUI();
  const lang = useLang();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <h1 className="font-serif text-4xl">404</h1>
        <p className="mt-4 text-[#4A3F35]">Article introuvable.</p>
        <Link to={`/${lang}/blog`} className="btn-ghost mt-8 inline-flex">{ui.backToBlog}</Link>
      </div>
    );
  }

  if (!post) return <div className="min-h-[60vh] flex items-center justify-center"><div className="loader" /></div>;

  const seo = {
    title: post.title,
    description: post.excerpt,
    og_image: post.cover_url,
  };

  return (
    <article data-testid="blog-post-page">
      <SEO seoData={seo} fallbackTitle={t(post.title)} />
      {post.cover_url && (
        <div className="w-full h-[60vh] overflow-hidden">
          <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.5 }} src={post.cover_url} alt={t(post.title)} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <Link to={`/${lang}/blog`} className="inline-flex items-center gap-2 text-sm text-[#4A3F35]/70 hover:text-[#C87A5E] transition mb-8">
          <ArrowLeft size={14} /> {ui.backToBlog}
        </Link>
        <div className="flex items-center gap-3 text-xs text-[#4A3F35]/60 mb-4">
          {post.category && <span className="text-[#C87A5E] uppercase tracking-wider">{post.category}</span>}
          {post.published_at && <span>{new Date(post.published_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { year: "numeric", month: "long", day: "numeric" })}</span>}
          {post.reading_time && <span className="flex items-center gap-1"><Clock size={11} /> {post.reading_time} {ui.minRead}</span>}
        </div>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tighter leading-tight font-light">{t(post.title)}</h1>
        {t(post.excerpt) && <p className="mt-6 text-xl text-[#4A3F35] leading-relaxed">{t(post.excerpt)}</p>}
        <div className="mt-12 prose-blog" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t(post.content), SANITIZE_CONFIG) }} />
        {post.author && <div className="mt-16 pt-8 border-t border-[#2D2A26]/10 text-sm text-[#4A3F35]/70">{ui.by} <span className="text-[#2D2A26] font-medium">{post.author}</span></div>}
      </div>
    </article>
  );
}
